import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";

import type { ChatToolCall, ChatToolCallStatus } from "../types/chat.types";
import { redactToolArgumentsJson } from "../utils/toolTracePii";
import { useCanViewSensitiveToolArguments } from "../hooks/useChatToolTraceFlag";

function statusIcon(status: ChatToolCallStatus): string {
  switch (status) {
    case "ok":
      return "✓";
    case "error":
      return "⚠";
    case "rejected":
    case "aborted":
      return "⊘";
    default:
      return "…";
  }
}

function annotationGlyphs(annotations?: ChatToolCall["annotations"]): string {
  if (!annotations) return "";
  const parts: string[] = [];
  if (annotations.readOnlyHint) parts.push("👁");
  if (annotations.destructiveHint) parts.push("⚠️");
  if (annotations.idempotentHint) parts.push("↻");
  return parts.length ? ` ${parts.join(" ")}` : "";
}

interface ToolCallTracePanelProps {
  toolCalls: ChatToolCall[];
}

const ToolCallTracePanel: React.FC<ToolCallTracePanelProps> = ({ toolCalls }) => {
  const { t } = useTranslation();
  const canViewSensitive = useCanViewSensitiveToolArguments();
  const [expanded, setExpanded] = useState(false);
  const [openArgs, setOpenArgs] = useState<Record<string, boolean>>({});

  const totalMs = useMemo(
    () =>
      toolCalls.reduce(
        (sum, tc) => sum + (tc.durationMs != null && tc.status !== "running" ? tc.durationMs : 0),
        0
      ),
    [toolCalls]
  );

  if (!toolCalls.length) return null;

  return (
    <div className="mb-2" data-testid="tool-call-trace-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-left text-[11px] text-gray-600 hover:bg-gray-100"
        aria-expanded={expanded}
        data-testid="tool-call-trace-chip"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" aria-hidden />
        )}
        <span>
          🔧 {toolCalls.length} {t("tools used")}
          {totalMs > 0 ? ` · ${totalMs} ms` : ""}
        </span>
      </button>
      {expanded ? (
        <ul
          className="mt-1 space-y-1 rounded-md border border-gray-100 bg-white px-2 py-1.5 text-[11px]"
          data-testid="tool-call-trace-list"
        >
          {toolCalls.map((tc) => {
            const argsOpen = openArgs[tc.id] ?? false;
            const isReadOnly = tc.annotations?.readOnlyHint === true;
            return (
              <li key={tc.id} className="border-b border-gray-50 pb-1 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-mono text-gray-800">
                    {tc.name}
                    {annotationGlyphs(tc.annotations)}
                  </span>
                  {tc.durationMs != null ? (
                    <span className="text-gray-500">{tc.durationMs} ms</span>
                  ) : null}
                  <span aria-hidden>{statusIcon(tc.status)}</span>
                </div>
                {tc.error ? (
                  <p className="mt-0.5 text-red-600">
                    {tc.error.message}
                    {tc.error.retryAfterSeconds
                      ? ` (${t("retry in {{s}}s", { s: tc.error.retryAfterSeconds })})`
                      : null}
                  </p>
                ) : null}
                <button
                  type="button"
                  className="mt-0.5 text-[10px] text-blue-600 hover:underline"
                  onClick={() => setOpenArgs((prev) => ({ ...prev, [tc.id]: !argsOpen }))}
                >
                  {argsOpen ? t("Hide arguments") : t("Show arguments")}
                </button>
                {argsOpen ? (
                  <pre className="mt-1 max-h-32 overflow-auto rounded bg-gray-50 p-1 font-mono text-[10px] text-gray-700">
                    {redactToolArgumentsJson(tc.arguments ?? {}, canViewSensitive)}
                  </pre>
                ) : null}
                {!isReadOnly && tc.result != null && tc.status === "ok" ? (
                  <details className="mt-0.5 text-[10px] text-gray-600">
                    <summary className="cursor-pointer hover:underline">{t("Result")}</summary>
                    <pre className="mt-0.5 max-h-24 overflow-auto rounded bg-gray-50 p-1 font-mono">
                      {typeof tc.result === "string"
                        ? tc.result
                        : JSON.stringify(tc.result, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default ToolCallTracePanel;
